import type { ReactNode } from 'react';
import {
  Box,
  Skeleton,
  Stack,
  useResponsive,
  useTheme
} from '@rabex-kit/rabex-ui';
import { P2P_MAX_CONTENT_WIDTH } from '../../../constants/layout';

/**
 * Shimmer placeholder for the mobile p2p page.
 *
 * Ported from the Android screen (presentation/p2p/shimmer/P2pShimmer.kt and
 * OrderItemsShimmer.kt) so the two clients load the same way — same blocks,
 * same sizes in dp/px, same 6px corner on every bar.
 *
 * One deliberate difference: the two panels sit in the order the web page
 * renders them (form first, so it lands on the right in rtl, order book on the
 * left) and split the width evenly, matching pages/P2p/Mobile. Otherwise the
 * real page would jump sideways when it replaces this.
 */

type BarProps = {
  width?: number | string;
  height: number;
  grow?: boolean;
};

/** One shimmer bar — the Box + colorShimmer + RoundedCornerShape(6.dp) pair. */
const Bar = ({ width, height, grow }: BarProps) => (
  <Skeleton
    variant="rounded"
    animation="pulse"
    width={width}
    height={height}
    sx={{
      borderRadius: '6px',
      flexGrow: grow ? 1 : 0,
      flexShrink: 0
    }}
  />
);

const Divider = () => {
  const { palette } = useTheme();
  return <Box sx={{ height: '1px', bgcolor: palette.base[200] }} />;
};

/** A row with one bar pinned to each edge. */
const SpreadRow = ({
  width = 60,
  height = 12
}: {
  width?: number;
  height?: number;
}) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Bar width={width} height={height} />
    <Bar width={width} height={height} />
  </Stack>
);

/** Left panel: the order book — two column headings over a stack of rows. */
const OrderBookSkeleton = () => (
  <Stack gap={3}>
    <Stack direction="row" justifyContent="space-between">
      <Stack gap={1}>
        <Bar width={40} height={12} />
        <Bar width={20} height={12} />
      </Stack>
      <Stack gap={1} alignItems="flex-end">
        <Bar width={40} height={12} />
        <Bar width={20} height={12} />
      </Stack>
    </Stack>
    <Stack gap={1}>
      {Array.from({ length: 9 }, (_, index) => index).map((index) => (
        <Bar key={index} width="100%" height={27} />
      ))}
    </Stack>
  </Stack>
);

/** Right panel: the order form — tabs, type, inputs, percent chips, button. */
const OrderFormSkeleton = () => (
  <Stack gap={2}>
    <Stack direction="row" gap={2}>
      <Bar height={32} grow />
      <Bar height={32} grow />
    </Stack>
    <Bar width="100%" height={32} />
    <Bar width="100%" height={40} />
    <Bar width="100%" height={40} />
    <Stack direction="row" gap={2}>
      {Array.from({ length: 4 }, (_, index) => index).map((index) => (
        <Bar key={index} height={20} grow />
      ))}
    </Stack>
    <Bar width="100%" height={40} />
    <Stack gap={2} pt={1}>
      <SpreadRow width={40} height={16} />
      <SpreadRow width={40} height={16} />
    </Stack>
    <Bar width="100%" height={40} />
  </Stack>
);

/**
 * One open-order card. The chip sits at the top of the inline-start edge,
 * overlaying the card the way the Box in OrderItemsShimmer does.
 */
const OrderItemSkeleton = () => {
  const { palette } = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '8px',
        bgcolor: palette.base[50],
        overflow: 'hidden'
      }}>
      <Stack
        p={4}
        gap={1}
        alignItems="flex-end"
        sx={{ '& > *': { width: '100%' } }}>
        <Stack gap={1} alignItems="flex-end">
          <Bar width={60} height={12} />
          <Bar width={60} height={12} />
        </Stack>
        <Stack gap={1} pt={0.5}>
          <SpreadRow />
          <SpreadRow />
        </Stack>
        <Divider />
        <Stack gap={1} py={0.5}>
          <SpreadRow />
          <SpreadRow />
        </Stack>
      </Stack>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          insetInlineStart: 0,
          py: 1,
          px: 2,
          bgcolor: palette.secondary[500],
          borderRadius: '8px 8px 4px 4px'
        }}>
        <Bar width={60} height={12} />
      </Box>
    </Box>
  );
};

/** Bottom section: the open-orders header, its filter row and two cards. */
const OpenOrdersSkeleton = () => (
  <Stack>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      height={52}
      px={2}>
      <Bar width={24} height={24} />
      <Stack direction="row" gap={2}>
        <Bar width={80} height={24} />
        <Bar width={80} height={24} />
      </Stack>
    </Stack>
    <Divider />
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      px={2}
      py={2}>
      <Bar width={80} height={16} />
      <Stack direction="row" alignItems="center" gap={2}>
        <Bar width={80} height={16} />
        <Bar width={20} height={20} />
      </Stack>
    </Stack>
    <Divider />
    <Stack gap={2} p={2}>
      <OrderItemSkeleton />
      <OrderItemSkeleton />
    </Stack>
  </Stack>
);

const P2pMobileSkeleton = () => (
  <>
    <Stack
      direction="row"
      alignItems="flex-start"
      gap="12px"
      px={1.5}
      sx={{ gridArea: 'tabs' }}>
      <Stack flex={1} minWidth={0}>
        <OrderFormSkeleton />
      </Stack>
      <Stack flex={1} minWidth={0}>
        <OrderBookSkeleton />
      </Stack>
    </Stack>
    <Stack sx={{ gridArea: 'openOrders' }}>
      <OpenOrdersSkeleton />
    </Stack>
  </>
);

/** A panel-shaped block on the page's card colour, for the wide layouts. */
const Panel = ({
  height,
  area,
  rows = 0,
  children
}: {
  height?: number | string;
  area?: string;
  rows?: number;
  children?: ReactNode;
}) => {
  const { palette } = useTheme();

  return (
    <Stack
      gap={1}
      p={2}
      bgcolor={palette.base[50]}
      sx={{ gridArea: area, height, minHeight: 0, overflow: 'hidden' }}>
      {Array.from({ length: rows }, (_, index) => index).map((index) => (
        <Bar key={index} width="100%" height={20} />
      ))}
      {children}
    </Stack>
  );
};

/**
 * Tablet and desktop: the same grid areas the real page fills, so nothing
 * moves when it lands. Desktop is order book | chart + form | market list;
 * tablet is the form beside the chart and book.
 */
export const P2pWideSkeleton = ({ isDesktop }: { isDesktop: boolean }) =>
  isDesktop ? (
    <>
      <Stack gap={0.5} sx={{ gridArea: 'orderBook', height: 760 }}>
        <Panel height={330} rows={11} />
        <Panel height={430} rows={14} />
      </Stack>
      <Panel area="market" height={760} rows={26} />
      <Stack gap={0.5} sx={{ gridArea: 'chart' }}>
        <Panel height={430} />
        <Panel height={330}>
          <OrderFormSkeleton />
        </Panel>
      </Stack>
      <Stack sx={{ gridArea: 'openOrders' }}>
        <Panel height={220} rows={5} />
      </Stack>
    </>
  ) : (
    <>
      <Stack sx={{ gridArea: 'orderForm' }}>
        <Panel height={520}>
          <OrderFormSkeleton />
        </Panel>
      </Stack>
      <Stack gap={0.5} sx={{ gridArea: 'chart' }}>
        <Panel height={323} />
        <Stack flexDirection="row" gap={0.5}>
          <Panel height={300} rows={10} />
          <Panel height={300} rows={10} />
        </Stack>
      </Stack>
      <Stack sx={{ gridArea: 'openOrders' }}>
        <Panel height={220} rows={5} />
      </Stack>
    </>
  );

type Matches = ReturnType<typeof useResponsive>['matches'];

/** The trading grid, shared with Layout/p2p so the skeleton sits exactly where the page does. */
export const p2pGridProps = (matches: Matches) => ({
  display: 'grid',
  alignItems: 'stretch',
  justifyContent: 'stretch',
  gridTemplateRows: 'minmax(min-content, max-content)',
  gap: matches({ xs: 1, lg: 0.5 }),
  gridTemplateColumns: matches({
    xs: '1fr',
    md: '323px 1fr',
    lg: '1fr 2fr 1fr'
  }),
  gridTemplateAreas: matches({
    xs: "'tabs''openOrders'",
    md: "'orderForm chart''openOrders openOrders'",
    lg: "'orderBook chart market' 'openOrders openOrders openOrders'"
  })
});

/** The market bar (containers/Prices) while it has nothing to show: same box, same height. */
const MarketBarSkeleton = () => {
  const { palette } = useTheme();
  const { matches } = useResponsive();
  const isDesktop = matches({ xs: false, lg: true });

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      px={matches({ xs: 2, lg: 4 })}
      bgcolor={palette.base[50]}
      sx={{
        height: matches({ xs: 66, lg: 72 }),
        border: `1px solid ${palette.base[200]}`
      }}>
      {isDesktop && <Bar width={40} height={40} />}
      <Bar width={204} height={isDesktop ? 52 : 40} grow={!isDesktop} />
      {isDesktop ? (
        Array.from({ length: 4 }, (_, index) => index).map((index) => (
          <Bar key={index} width={120} height={36} />
        ))
      ) : (
        <>
          <Bar width={40} height={40} />
          <Bar width={40} height={40} />
        </>
      )}
    </Stack>
  );
};

/**
 * The whole p2p page while it loads: market bar, then the panels in the page's
 * own grid. The route fallback (while the page's code downloads) and the page
 * (while the market list loads) both draw this one, so the shimmer simply
 * continues instead of switching to a different shape halfway.
 */
export const P2pPageSkeleton = () => {
  const { matches } = useResponsive();
  const layout = matches({ xs: 'mobile', md: 'tablet', lg: 'desktop' });

  return (
    <Stack
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="در حال بارگذاری"
      flexDirection="column"
      gap={matches({ xs: 1, lg: 0.5 })}
      width="100%"
      maxWidth={P2P_MAX_CONTENT_WIDTH}
      mx="auto">
      <MarketBarSkeleton />
      <Box flexGrow={1} sx={p2pGridProps(matches)}>
        {layout === 'mobile' ? (
          <P2pMobileSkeleton />
        ) : (
          <P2pWideSkeleton isDesktop={layout === 'desktop'} />
        )}
      </Box>
    </Stack>
  );
};

export default P2pMobileSkeleton;
